import { isNullish, nextGuid, noOp } from '@breezy/shared'
import { faSearch, IconDefinition } from '@fortawesome/pro-regular-svg-icons'
import { faXmark } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Drawer, Input, InputRef, Modal } from 'antd'
import classNames from 'classnames'
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import ReactDOM from 'react-dom/client'
import { LoadingSpinner } from '../../components/LoadingSpinner'
import { BzCloseButton, BzCloseButtonType } from '../../elements/BzCloseButton/BzCloseButton'
import { useIntercom } from '../../hooks/useIntercom'
import useIsMobile from '../../hooks/useIsMobile'
import { Z_INDEX_ONSITE_MODAL } from '../../layout/zIndex'
import { useHidingHeader } from '../OnsitePage/onsitePageUtils'
import './OnsiteModal.less'

type OnsiteModalSize = 'small' | 'large' | 'default' | 'large-width' | 'xlarge' | 'xlarge-width' | 'full'

type OnsiteModalProps = React.PropsWithChildren<{
  onClose?: () => void
  size?: OnsiteModalSize
  drawer?: boolean
  open?: boolean
  className?: string
  modalClassName?: string
  modalZIndexClassName?: string
  testId?: string
}>

type OnsiteModalFooterProps = {
  className?: string
  onCancel: () => void
  onSubmit?: () => void
  cancelText?: string
  submitText?: string
  submitDisabled?: boolean
  loading?: boolean
  danger?: boolean
  hideCancelButton?: boolean
  hideSubmitButton?: boolean
}

export const OnsiteModalFooter = React.memo<OnsiteModalFooterProps>(
  ({
    onCancel,
    onSubmit,
    className,
    cancelText = 'Cancel',
    submitText = 'Save',
    submitDisabled,
    loading,
    danger,
    hideCancelButton,
    hideSubmitButton,
  }) => {
    return (
      <div className={classNames('mt-2 flex flex-row justify-end gap-x-3', className)}>
        {!hideCancelButton && (
          <Button block size="large" onClick={() => onCancel()}>
            {cancelText}
          </Button>
        )}
        {!hideSubmitButton && (
          <Button
            block
            loading={loading}
            danger={danger}
            type="primary"
            size="large"
            onClick={onSubmit ? () => onSubmit() : noOp}
            htmlType="submit"
            disabled={submitDisabled}
          >
            {submitText}
          </Button>
        )}
      </div>
    )
  },
)

OnsiteModalFooter.displayName = 'OnsiteModalFooter'

type OnsiteModalHeaderProps = React.PropsWithChildren<{
  className?: string
  bordered?: boolean
  headerRef?: React.RefObject<HTMLDivElement>
}>

export const OnsiteModalHeader = React.memo<OnsiteModalHeaderProps>(({ children, className, bordered, headerRef }) => {
  const isMobile = useIsMobile()

  return (
    <div
      ref={headerRef}
      className={classNames(
        'text-3xl font-semibold text-bz-gray-1000',
        isMobile ? 'pb-4' : 'pb-6',
        {
          'border-0 border-b-4 border-solid border-b-bz-gray-400': bordered,
          'mx-[-16px] mb-4 px-4': bordered && isMobile,
          'mx-[-24px] mb-6 px-6': bordered && !isMobile,
        },
        className,
      )}
    >
      {children}
    </div>
  )
})

OnsiteModalHeader.displayName = 'OnsiteModalHeader'

type OnsiteModalContentProps = React.PropsWithChildren<
  {
    footer?: React.ReactNode
    onBack?: () => void
    onClose?: () => void
    isLoading?: boolean
    disabled?: boolean
    header?: React.ReactNode
    headerBordered?: boolean
    headerAlwaysUp?: boolean
    subtitle?: React.ReactNode
    alwaysShowScrollbar?: boolean
    unpadBottom?: boolean
    onScroll?: (scrollTop: number) => void
    searchInputRef?: React.RefObject<InputRef>
    className?: string
    searchPlaceholder?: string
    childWrapperClassName?: string
    closeButtonType?: BzCloseButtonType
    searchIcon?: IconDefinition
  } & (
    | {
        searchTerm?: never
        onSearch?: never
      }
    | {
        searchTerm: string
        onSearch: (searchTerm: string) => void
      }
  )
>

export const OnsiteModalContent = React.memo<OnsiteModalContentProps>(
  ({
    footer,
    onBack,
    onClose,
    isLoading,
    children,
    header,
    headerBordered,
    headerAlwaysUp,
    subtitle,
    searchTerm,
    onSearch,
    alwaysShowScrollbar,
    unpadBottom,
    onScroll,
    disabled = false,
    searchInputRef,
    className,
    searchPlaceholder = 'Search...',
    childWrapperClassName,
    closeButtonType = 'default',
    searchIcon,
  }) => {
    const isMobile = useIsMobile()

    const bodyRef = useRef<HTMLDivElement>(null)

    const headerRef = useRef<HTMLDivElement>(null)

    const [searchFocused, setSearchFocused] = useState(false)

    // biome-ignore lint/correctness/useExhaustiveDependencies: ESLint Migration
    useLayoutEffect(() => {
      if (bodyRef.current && onScroll) {
        const container = bodyRef.current
        const handler = () => {
          onScroll(container.scrollTop)
        }
        container.addEventListener('scroll', handler)
        return () => container?.removeEventListener('scroll', handler)
      }
    }, [isLoading, onScroll])

    const { hidingUpperHeader, isHeaderOutOfView } = useHidingHeader({
      bodyRef,
      headerElement: headerRef.current,
      upperHeader: header,
      bottomMargin: isMobile ? 16 : 24,
    })

    const collapseSearch = isHeaderOutOfView && !searchFocused

    if (isLoading) {
      return <LoadingSpinner />
    }
    return (
      <>
        <div className={classNames('relative flex flex-row items-center', isMobile ? 'mb-4' : 'mb-6', className)}>
          {onClose && <BzCloseButton onClose={onClose} onBack={onBack} disabled={disabled} type={closeButtonType} />}
          {hidingUpperHeader}
          {/* This div is to counter-balance the close button so the header can be properly centered */}
          <div className="h-10 w-10" />
          {onSearch && (
            <Input
              allowClear={
                collapseSearch
                  ? false
                  : {
                      clearIcon: (
                        <FontAwesomeIcon
                          icon={faXmark}
                          className="mr-1 flex items-center justify-center text-base text-bz-gray-1000"
                        />
                      ),
                    }
              }
              prefix={<FontAwesomeIcon icon={searchIcon || faSearch} className="mr-2 text-bz-gray-700" />}
              className={classNames('absolute inset-y-0 right-0 h-10 rounded-full text-base transition-all')}
              style={{
                width: collapseSearch ? '40px' : 'calc(100% - 72px)',
              }}
              placeholder={searchPlaceholder}
              data-testid="onsiteModalSearchInput"
              ref={searchInputRef}
              value={searchTerm}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              onChange={e => onSearch(e.target.value)}
            />
          )}
        </div>

        <div className={classNames('relative flex min-h-0 flex-1 flex-col', childWrapperClassName)}>
          <div
            ref={bodyRef}
            className={classNames(
              'flex min-h-0 flex-1 flex-col overflow-x-hidden',
              alwaysShowScrollbar ? 'overflow-y-scroll' : 'overflow-y-auto',
              isMobile ? 'mx-[-1rem] px-4' : 'mx-[-1.5rem] px-6',
              {
                [isMobile ? 'pb-4' : 'pb-6']: !unpadBottom,
              },
            )}
          >
            {header && !headerAlwaysUp ? (
              <OnsiteModalHeader bordered={headerBordered} headerRef={headerRef}>
                {header}
                {subtitle ? <div className="mt-2 text-base font-normal">{subtitle}</div> : null}
              </OnsiteModalHeader>
            ) : null}

            {children}
          </div>
        </div>
        {footer ? (
          <div
            className={classNames(
              'flex min-w-0 flex-col border-0 border-t-4 border-solid border-t-bz-gray-400 pt-2',
              isMobile ? 'mx-[-1rem] px-4 pb-4' : 'mx-[-1.5rem] px-6 pb-6',
            )}
          >
            {footer}
          </div>
        ) : null}
      </>
    )
  },
)

OnsiteModalContent.displayName = 'OnsiteModalContent'

type OnsiteBasicModalProps = OnsiteModalProps & {
  header?: string
  footer?: React.ReactNode
  secondaryFooter?: React.ReactNode
  onBack?: () => void
  headerBordered?: boolean
}

export const OnsiteBasicModal = React.memo<OnsiteBasicModalProps>(
  ({ header, footer, secondaryFooter, onClose, children, headerBordered, onBack, ...passthrough }) => {
    const isMobile = useIsMobile()
    return (
      <OnsiteModal onClose={onClose} {...passthrough}>
        <OnsiteModalContent
          unpadBottom={!!secondaryFooter}
          onClose={onClose}
          footer={footer}
          onBack={onBack}
          header={header}
          headerBordered={headerBordered}
        >
          <div className={classNames('text-base')}>{children}</div>
          {secondaryFooter && (
            <div
              className={classNames(
                'mt-4 flex flex-row items-center border-0 border-t-4 border-solid border-t-bz-gray-400 py-3',
                isMobile ? 'mx-[-1rem] px-4' : 'mx-[-1.5rem] px-6',
              )}
            >
              {secondaryFooter}
            </div>
          )}
        </OnsiteModalContent>
      </OnsiteModal>
    )
  },
)

OnsiteBasicModal.displayName = 'OnsiteBasicModal'

export type OnsiteConfirmModalProps = Omit<OnsiteBasicModalProps, 'onClose' | 'footer'> &
  Omit<OnsiteModalFooterProps, 'submitText' | 'submitDisabled' | 'onSubmit'> & {
    onConfirm?: () => void
    confirmText?: string
    confirmDisabled?: boolean
  }

export const OnsiteConfirmModal = React.memo<OnsiteConfirmModalProps>(
  ({
    // Our props are basically the union of the modal props and the footer props. We can only spread one, and since I'm
    // renaming a few of the footer props I opted for those to be the ones I write out explicitly.
    onCancel,
    onConfirm,
    children,
    danger,
    cancelText,
    confirmText,
    loading,
    hideCancelButton,
    hideSubmitButton,
    confirmDisabled,
    ...modalPassthrough
  }) => (
    <OnsiteBasicModal
      onClose={onCancel}
      {...modalPassthrough}
      footer={
        <OnsiteModalFooter
          danger={danger}
          onCancel={onCancel}
          onSubmit={onConfirm}
          cancelText={cancelText}
          submitText={confirmText}
          loading={loading}
          hideCancelButton={hideCancelButton}
          hideSubmitButton={hideSubmitButton}
          submitDisabled={confirmDisabled}
        />
      }
    >
      {children}
    </OnsiteBasicModal>
  ),
)

OnsiteConfirmModal.displayName = 'OnsiteConfirmModal'

const OnsiteModalInner = React.memo<OnsiteModalProps>(
  ({ onClose, children, size = 'default', open = true, modalClassName, modalZIndexClassName, testId }) => {
    // It's insane that this is all necessary. For the antd modal, it doesn't call your "onClose" when you outside
    // click. Instead it just totally closes it. This is how we can work around the problem: disable their "on outside
    // click", hook into the modal when it's created, insert our own "mask" component with our own click event, and use
    // the onClose.
    const wrapClass = useMemo(() => `onsite-modal-inner-wrap-${nextGuid()}`, [])

    useLayoutEffect(() => {
      // If we're not open, don't do it. Otherwise, when we close we might just create the mask again.
      if (!open) {
        return
      }
      const wrap = document.querySelector(`.${wrapClass}`)
      if (wrap) {
        const modal = wrap.querySelector('.ant-modal')
        if (modal) {
          const ourRoot = document.createElement('div')
          ourRoot.className = 'onsite-inner-modal-custom-mask'

          wrap.insertBefore(ourRoot, modal)

          const root = ReactDOM.createRoot(ourRoot)
          root.render(<div className="fixed inset-0" onClick={() => onClose?.()} />)
        }
      }
      return () => {
        const wrap = document.querySelector(`.${wrapClass}`)
        if (wrap) {
          wrap.querySelector('.onsite-inner-modal-custom-mask')?.remove()
        }
      }
    }, [wrapClass, onClose, open])
    return (
      <Modal
        data-testid={testId}
        open={open}
        centered
        maskClosable={false}
        closeIcon={false}
        onCancel={() => onClose?.()}
        className={classNames(
          'onsite-modal',
          {
            'w-[75%]': size === 'xlarge-width',
            'h-[90%] w-[75%]': size === 'xlarge',
            'h-[90%] w-[75%] max-w-[600px]': size === 'large',
            'w-[600px] min-w-[600px]': size === 'large-width',
            'relative max-h-[90%]': size === 'default',
            'max-w-[390px]': size === 'small',
            // For some reason Tailwind defines min-h-screen and w-screen but not min-w-screen
            'full-screen-onsite-modal h-screen min-h-screen w-screen min-w-[100vw]': size === 'full',
          },
          modalClassName,
        )}
        footer={null}
        wrapClassName={classNames(
          `${modalZIndexClassName ?? `z-[${Z_INDEX_ONSITE_MODAL}]`} onsite-modal-wrapper`,
          wrapClass,
        )}
        classNames={{
          mask: modalZIndexClassName ?? `z-[${Z_INDEX_ONSITE_MODAL}]`,
        }}
      >
        <div className="flex max-h-full min-h-0 min-w-0 flex-1 flex-col overflow-auto px-6 pt-6">{children}</div>
      </Modal>
    )
  },
)

const OnsiteModalDrawer = React.memo<OnsiteModalProps>(
  ({ onClose, children, open = true, className, modalClassName, testId }) => {
    const isMobile = useIsMobile()
    // This is all very similar to the stuff above for the modal but the drawer works slightly differently. Still very
    // sad.
    const wrapClass = useMemo(() => `onsite-modal-drawer-wrap-${nextGuid()}`, [])
    useLayoutEffect(() => {
      setTimeout(() => {
        if (!open) {
          return
        }
        const wrap = document.querySelector(`.${wrapClass}`)
        if (wrap) {
          const drawer = wrap.querySelector('.ant-drawer-content-wrapper')
          if (drawer) {
            const ourRoot = document.createElement('div')
            ourRoot.className = 'onsite-modal-drawer-custom-mask'

            wrap.insertBefore(ourRoot, drawer)

            const root = ReactDOM.createRoot(ourRoot)
            root.render(<div className="absolute inset-0" onClick={() => onClose?.()} />)
          }
        }
      }, 0)
      return () => {
        setTimeout(() => {
          const wrap = document.querySelector(`.${wrapClass}`)
          if (wrap) {
            const customMask = wrap.querySelector('.onsite-modal-drawer-custom-mask')
            customMask?.remove()
          }
        }, 0)
      }
    }, [wrapClass, onClose, open])

    useIntercom({ isLauncherVisible: false })

    return (
      <Drawer
        data-testid={testId}
        open={open}
        maskClosable={false}
        onClose={() => onClose?.()}
        placement="bottom"
        closeIcon={false}
        rootClassName={classNames('onsite-modal-drawer onsite-modal', wrapClass, {
          'onsite-modal-drawer-open': open,
        })}
        height="auto"
      >
        <div
          className={classNames(
            'flex min-h-0 flex-1 flex-col overflow-auto py-4',
            isMobile ? 'px-4' : 'px-6',
            className,
            modalClassName,
          )}
        >
          {children}
        </div>
      </Drawer>
    )
  },
)

export const OnsiteModal = React.memo<OnsiteModalProps>(
  ({ children, onClose, open = true, testId, drawer, ...rest }) => {
    const isMobile = useIsMobile()

    if (isNullish(drawer) ? isMobile : drawer) {
      return (
        <OnsiteModalDrawer
          onClose={onClose}
          open={open}
          className={rest.className}
          modalClassName={rest.modalClassName}
          testId={testId}
        >
          {children}
        </OnsiteModalDrawer>
      )
    }

    if (isMobile) {
      return createPortal(
        <div
          data-testid={testId}
          className={classNames(
            'onsite-modal fixed inset-0 bg-white transition-transform duration-500',
            // 110% instead of 100% because there's a box shadow that spills out.
            open ? 'translate-y-0' : 'translate-y-[110%]',
            rest.className,
            rest.modalClassName,
            rest.modalZIndexClassName,
          )}
        >
          <div className="flex min-h-0 flex-1 flex-col p-4">{children}</div>
        </div>,
        document.body,
      )
    }
    return (
      <OnsiteModalInner open={open} {...rest} onClose={onClose} testId={testId}>
        {children}
      </OnsiteModalInner>
    )
  },
)

OnsiteModal.displayName = 'OnsiteModal'
